import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { CsvExportButton } from '../../components/data/CsvExportButton.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency, formatNumber } from '../../utils/formatters.js';

import { CostCenterModal } from './CostCenterModal.jsx';

const CSV_COLUMNS = [
  { key: 'code', label: 'Code' },
  { key: 'name', label: 'Name' },
  { key: 'budgetAmount', label: 'Budget' },
  { key: 'actual', label: 'Actual' },
  { key: 'remaining', label: 'Remaining' },
  { key: 'currency', label: 'Currency' },
];

function utilization(r) {
  const b = Number(r.budgetAmount) || 0;
  if (b <= 0) return 0;
  return Math.round((Number(r.actual) / b) * 100);
}

export function CostCentersPage() {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const report = useQuery({
    queryKey: ['finance', 'cost-center-report'],
    queryFn: () => financeApi.costCenterReport().then((r) => r.items.costCenters),
  });
  const all = useQuery({
    queryKey: ['finance', 'cost-centers'],
    queryFn: () => financeApi.listCostCenters().then((r) => r.items),
  });

  const rows = report.data || [];
  const totalBudget = rows.reduce((acc, r) => acc + (Number(r.budgetAmount) || 0), 0);
  const totalActual = rows.reduce((acc, r) => acc + (Number(r.actual) || 0), 0);
  const activeById = new Map((all.data || []).map((c) => [c.id, c.isActive]));

  function openNew() {
    setEditing(null);
    setModalOpen(true);
  }
  function openEdit(cc) {
    setEditing((all.data || []).find((c) => c.id === cc.id) || cc);
    setModalOpen(true);
  }

  const columns = [
    { key: 'code', label: t('cc.col.code') },
    { key: 'name', label: t('cc.col.name') },
    { key: 'budgetAmount', label: t('cc.col.budget'), align: 'right', render: (r) => formatCurrency(r.budgetAmount, r.currency) },
    { key: 'actual', label: t('cc.col.actual'), align: 'right', render: (r) => formatCurrency(r.actual, r.currency) },
    {
      key: 'remaining',
      label: t('cc.col.remaining'),
      align: 'right',
      render: (r) => (
        <strong style={{ color: Number(r.remaining) < 0 ? 'var(--color-danger)' : 'inherit' }}>
          {formatCurrency(r.remaining, r.currency)}
        </strong>
      ),
    },
    {
      key: 'util',
      label: t('cc.col.utilization'),
      render: (r) => {
        const pct = utilization(r);
        return (
          <div className="cc-bar-wrap">
            <div className="cc-bar">
              <div className={`cc-bar-fill${pct > 100 ? ' over' : ''}`} style={{ width: `${Math.min(100, pct)}%` }} />
            </div>
            <span className="cc-bar-label">{pct}%</span>
          </div>
        );
      },
    },
    {
      key: 'status',
      label: t('col.status'),
      render: (r) => (activeById.get(r.id) === false
        ? <Badge variant="neutral">{t('cc.inactive')}</Badge>
        : <Badge variant="success">{t('cc.active')}</Badge>),
    },
    {
      key: 'actions',
      label: '',
      render: (r) => (
        <Button size="sm" variant="ghost" onClick={() => openEdit(r)}>{t('cc.action.edit')}</Button>
      ),
    },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('cc.title')}</h1>
          <p className="muted">{t('cc.subtitle')}</p>
        </div>
        <div className="page-actions">
          <CsvExportButton rows={rows} columns={CSV_COLUMNS} filename="kostenstellen" />
          <Button variant="primary" onClick={openNew}>{t('cc.action.new')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('cc.metric.count')} value={String(rows.length)} />
        <MetricCard label={t('cc.metric.budget')} value={formatCurrency(totalBudget)} />
        <MetricCard label={t('cc.metric.actual')} value={formatCurrency(totalActual)} />
        <MetricCard
          label={t('cc.metric.utilization')}
          value={`${totalBudget > 0 ? Math.round((totalActual / totalBudget) * 100) : 0}%`}
          trendVariant={totalActual > totalBudget ? 'danger' : 'success'}
        />
      </div>

      <Table columns={columns} rows={rows} isLoading={report.isLoading} emptyMessage={t('cc.empty')} />

      <CostCenterModal open={modalOpen} onClose={() => setModalOpen(false)} editing={editing} />
    </section>
  );
}
